import type { Instance } from '@empathybroker/tsclient-play/dist/models/instance/instance'
import type {
  Identifiable,
  QueryRanking,
  QueryRankingConfigDTO,
  QueryRankingConfigWithIdDTO,
  QueryRankingElementRankingDTO,
  QueryRankingFactory,
  QueryRankingHiddenElementDTO,
  QueryRankingPinnedElementDTO,
} from '@empathyco/playboard-core-domain'
import type { Store } from 'vuex/types'
import type { QueryRankingDomainService } from '../interfaces/domain/query-ranking-domain.service'
import { inject, injectable } from 'inversify'
import { TYPES } from '../../config/inversify.types'
import { CONFIG_CONSTS } from '../../store/config/config.consts'
import { ConfigurationsUtils } from '../../utils/configurations.utils'

@injectable()
export class PlatformQueryRankingDomainService implements QueryRankingDomainService {
  public constructor(
    @inject(TYPES.Store)
    protected store: Store<unknown>,
    @inject(TYPES.QueryRankingFactory) protected factory: QueryRankingFactory,
  ) {}

  getEmptyInstance(): QueryRanking {
    return this.factory()
  }

  isValid(item: QueryRanking): boolean {
    const hasQueryRankingConfigs =
      item.productsRanking.length > 0 ||
      item.attributesRanking.length > 0 ||
      item.pinnedProducts.length > 0 ||
      item.hiddenAttributes.length > 0 ||
      item.hiddenProducts.length > 0

    const areProductRankingInConflict = item.pinnedProducts.some(
      productRanking =>
        this.isQueryRankingConfigInConflict(item.productsRanking, productRanking) ||
        this.isQueryRankingConfigInConflict(item.hiddenProducts, productRanking),
    )

    return (
      !!item.title &&
      (item.criteria.queries.length > 0 ||
        (!!item.criteria.browseField && !!item.criteria.browseValue)) &&
      hasQueryRankingConfigs &&
      !areProductRankingInConflict
    )
  }

  getProductIdField(): string {
    const { catalog }: Instance =
      this.store.getters[`${CONFIG_CONSTS.moduleName}/${CONFIG_CONSTS.getters.currentInstance}`]

    return catalog?.resultMappings.id ?? ''
  }

  getPinnedProductIdField(): string {
    const { catalog }: Instance =
      this.store.getters[`${CONFIG_CONSTS.moduleName}/${CONFIG_CONSTS.getters.currentInstance}`]

    return catalog?.resultMappings.customPinnedProductFieldId ?? catalog?.resultMappings.id ?? ''
  }

  createElementRanking(
    elementRanking: Partial<QueryRankingElementRankingDTO> &
      Required<Pick<QueryRankingElementRankingDTO, 'action' | 'fieldName' | 'fieldValue'>>,
  ): QueryRankingElementRankingDTO {
    return {
      action: elementRanking.action,
      fieldName: elementRanking.fieldName,
      fieldValue: elementRanking.fieldValue,
      filters: this.createRankingElementFilters(elementRanking.filters),
      schedule: this.createRankingElementSchedule(elementRanking.schedule),
    }
  }

  createHiddenElement(
    hiddenElement: Partial<QueryRankingHiddenElementDTO> &
      Required<Pick<QueryRankingHiddenElementDTO, 'action' | 'fieldName' | 'fieldValue'>>,
  ): QueryRankingHiddenElementDTO {
    return {
      action: hiddenElement.action,
      fieldName: hiddenElement.fieldName,
      fieldValue: hiddenElement.fieldValue,
      filters: this.createRankingElementFilters(hiddenElement.filters),
      schedule: this.createRankingElementSchedule(hiddenElement.schedule),
    }
  }
  createProductRanking(
    productRanking: Omit<Partial<QueryRankingElementRankingDTO>, 'fieldName'> &
      Required<Pick<QueryRankingElementRankingDTO, 'action' | 'fieldValue'>>,
  ): QueryRankingElementRankingDTO {
    return this.createElementRanking({
      ...productRanking,
      fieldName: this.getProductIdField(),
    })
  }

  createPinnedProduct(
    pinnedProduct: Omit<Partial<QueryRankingPinnedElementDTO>, 'fieldName'> &
      Required<Pick<QueryRankingPinnedElementDTO, 'action' | 'fieldValue' | 'position'>>,
  ): QueryRankingPinnedElementDTO {
    return {
      action: pinnedProduct.action,
      fieldName: this.getPinnedProductIdField(),
      fieldValue: pinnedProduct.fieldValue,
      position: pinnedProduct.position,
      filters: this.createRankingElementFilters(pinnedProduct.filters),
      schedule: this.createRankingElementSchedule(pinnedProduct.schedule),
    }
  }

  createHiddenProduct(
    hiddenProduct: Omit<Partial<QueryRankingHiddenElementDTO>, 'fieldName'> &
      Required<Pick<QueryRankingHiddenElementDTO, 'action' | 'fieldValue'>>,
  ): QueryRankingHiddenElementDTO {
    return {
      action: hiddenProduct.action,
      fieldName: this.getProductIdField(),
      fieldValue: hiddenProduct.fieldValue,
      filters: this.createRankingElementFilters(hiddenProduct.filters),
      schedule: this.createRankingElementSchedule(hiddenProduct.schedule),
    }
  }

  areProductRankingFieldsInConflict(
    queryRanking: QueryRanking,
    productRanking: Pick<QueryRankingConfigWithIdDTO, 'fieldName' | 'fieldValue' | 'schedule'>,
  ): boolean {
    return this.isQueryRankingConfigInConflict(
      [
        ...queryRanking.productsRanking,
        ...queryRanking.pinnedProducts,
        ...queryRanking.hiddenProducts,
      ],
      productRanking,
    )
  }

  areAttributeRankingFieldsInConflict(
    queryRanking: QueryRanking,
    attributeRanking: Pick<
      QueryRankingConfigWithIdDTO,
      'id' | 'fieldName' | 'fieldValue' | 'schedule'
    >,
  ): boolean {
    return this.isQueryRankingConfigInConflict(
      [...queryRanking.attributesRanking, ...queryRanking.hiddenAttributes],
      attributeRanking,
    )
  }

  arePinnedProductFieldsInConflict(
    queryRanking: QueryRanking,
    pinnedProduct: Pick<
      QueryRankingPinnedElementDTO,
      'fieldName' | 'fieldValue' | 'schedule' | 'position'
    > &
      Partial<Identifiable>,
  ): boolean {
    return this.isQueryRankingConfigInConflict(
      [
        ...queryRanking.productsRanking,
        ...queryRanking.pinnedProducts,
        ...queryRanking.hiddenProducts,
      ],
      pinnedProduct,
    )
  }

  areHiddenProductFieldsInConflict(
    queryRanking: QueryRanking,
    hiddenProduct: Pick<QueryRankingConfigWithIdDTO, 'fieldName' | 'fieldValue' | 'schedule'>,
  ): boolean {
    return this.isQueryRankingConfigInConflict(
      [
        ...queryRanking.hiddenProducts,
        ...queryRanking.productsRanking,
        ...queryRanking.pinnedProducts,
      ],
      hiddenProduct,
    )
  }

  areHiddenAttributeFieldsInConflict(
    queryRanking: QueryRanking,
    hiddenProduct: Pick<
      QueryRankingConfigWithIdDTO,
      'id' | 'fieldName' | 'fieldValue' | 'schedule'
    >,
  ): boolean {
    return this.isQueryRankingConfigInConflict(
      [...queryRanking.attributesRanking, ...queryRanking.hiddenAttributes],
      hiddenProduct,
    )
  }

  isPinnedProductPositionInConflict(
    queryRanking: QueryRanking,
    pinnedProduct: Pick<QueryRankingPinnedElementDTO, 'position' | 'schedule'> &
      Partial<Identifiable>,
  ): boolean {
    const filteredQueryRankingConfigs = this.getFilteredQueryRankingConfigs(
      queryRanking.pinnedProducts,
      pinnedProduct,
    ) as Array<QueryRankingPinnedElementDTO>
    return filteredQueryRankingConfigs.some(existingQueryRankingConfig =>
      this.arePinnedProductPositionsInConflict(existingQueryRankingConfig, pinnedProduct),
    )
  }

  areConfigFieldsInConflict(
    queryRankingConfig1: Pick<QueryRankingConfigDTO, 'fieldName' | 'fieldValue' | 'schedule'>,
    queryRankingConfig2: Pick<QueryRankingConfigDTO, 'fieldName' | 'fieldValue' | 'schedule'>,
  ): boolean {
    const arePeriodsOverlapped = ConfigurationsUtils.areQueryRankingConfigSchedulesOverlapped(
      queryRankingConfig1.schedule,
      queryRankingConfig2.schedule,
    )

    return (
      queryRankingConfig1.fieldName === queryRankingConfig2.fieldName &&
      queryRankingConfig1.fieldValue === queryRankingConfig2.fieldValue &&
      arePeriodsOverlapped
    )
  }

  arePinnedProductPositionsInConflict(
    pinnedProduct1: Pick<QueryRankingPinnedElementDTO, 'position' | 'schedule'>,
    pinnedProduct2: Pick<QueryRankingPinnedElementDTO, 'position' | 'schedule'>,
  ): boolean {
    const arePeriodsOverlapped = ConfigurationsUtils.areQueryRankingConfigSchedulesOverlapped(
      pinnedProduct1.schedule,
      pinnedProduct2.schedule,
    )

    return pinnedProduct1.position === pinnedProduct2.position && arePeriodsOverlapped
  }

  private createRankingElementFilters(
    filters?: QueryRankingConfigDTO['filters'],
  ): QueryRankingConfigDTO['filters'] {
    return filters ?? {}
  }

  private createRankingElementSchedule(
    schedule?: QueryRankingConfigDTO['schedule'],
  ): QueryRankingConfigDTO['schedule'] {
    return (
      schedule ?? {
        activationDate: undefined,
        deactivationDate: undefined,
      }
    )
  }

  private isQueryRankingConfigInConflict(
    queryRankingConfigs: Array<QueryRankingConfigDTO>,
    queryRankingConfig: Pick<QueryRankingConfigWithIdDTO, 'fieldName' | 'fieldValue' | 'schedule'> &
      Partial<Identifiable>,
  ): boolean {
    const filteredQueryRankingConfigs = this.getFilteredQueryRankingConfigs(
      queryRankingConfigs,
      queryRankingConfig,
    )
    return filteredQueryRankingConfigs.some(existingQueryRankingConfig =>
      this.areConfigFieldsInConflict(existingQueryRankingConfig, queryRankingConfig),
    )
  }

  private getFilteredQueryRankingConfigs(
    queryRankingConfigs: Array<QueryRankingConfigDTO>,
    queryRankingConfig: Partial<Identifiable>,
  ): Array<QueryRankingConfigDTO> {
    if (this.isQueryRankingElementWithId(queryRankingConfig)) {
      return ConfigurationsUtils.removeQueryRankingElementById(
        queryRankingConfigs,
        queryRankingConfig.id,
      )
    }

    return queryRankingConfigs
  }

  private isQueryRankingElementWithId(
    queryRankingConfig: Partial<Identifiable>,
  ): queryRankingConfig is QueryRankingConfigWithIdDTO {
    return !!queryRankingConfig.id
  }
}
